import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, Check, Lock, Eye, EyeOff, Quote } from 'lucide-react';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    // Check if we have a valid recovery session from the reset link
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session) {
          setIsValidSession(true);
        } else {
          setError('Invalid or expired reset link. Please request a new one.');
        }
      } catch (err) {
        setError('Failed to verify reset link. Please try again.');
      } finally {
        setCheckingSession(false);
      }
    };

    // Listen for auth state change (PKCE flow sets session from URL hash)
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setIsValidSession(true);
        setCheckingSession(false);
      }
    });

    checkSession();
    return () => subscription?.unsubscribe();
  }, []);

  const validateForm = () => {
    if (!password?.trim()) return 'Please enter a new password.';
    if (password?.length < 8) return 'Password must be at least 8 characters long.';
    if (!/(?=.*[a-zA-Z])(?=.*[0-9])/?.test(password)) return 'Password must contain at least one letter and one number.';
    if (!confirmPassword?.trim()) return 'Please confirm your new password.';
    if (password !== confirmPassword) return 'Passwords do not match.';
    return '';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      const { error: updateError } = await supabase?.auth?.updateUser({ password });
      if (updateError) throw updateError;
      setSuccess(true);
      // Auto-redirect after 3 seconds
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      console.error('Password update error:', err);
      setError(err?.message || 'Failed to update password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative py-8 px-4 sm:px-6 lg:px-8">
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/assets/images/FortifiedMindSplash1-1769379659227.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          filter: 'brightness(0.7)'
        }}
      />
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative z-10 w-full max-w-7xl mx-auto flex justify-start">
        <div className="w-full max-w-2xl space-y-6">
          <div className="text-center">
            <div className="mx-auto h-20 w-20 bg-white/50 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 shadow-2xl">
              <svg className="w-10 h-10 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight drop-shadow-2xl">FORTIFIED MIND</h1>
          </div>

          <div className="bg-primary/20 backdrop-blur-sm rounded-2xl shadow-2xl p-5 sm:p-6 text-white border border-white/20">
            <div className="flex items-start space-x-4">
              <Quote className="w-7 h-7 text-primary/80 flex-shrink-0 mt-1" />
              <blockquote className="text-base italic leading-relaxed">
                "None but those who have <span className="font-bold text-warning bg-warning/20 px-1 rounded">fortified</span> the <span className="font-bold text-warning bg-warning/20 px-1 rounded">mind</span> with the truths of the Bible will stand through the last great conflict."
                <cite className="block text-white/70 text-sm font-medium not-italic mt-2">— Great Controversy pg. 593</cite>
              </blockquote>
            </div>
          </div>

          <div className="backdrop-blur-md rounded-2xl shadow-2xl p-6 sm:p-8 space-y-5 border border-white/30 bg-[rgba(255,255,255,0.6)]">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Lock className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Set New Password</h2>
              </div>
              <p className="text-sm text-foreground/70">Create a strong new password for your account.</p>
            </div>

            {checkingSession ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Verifying reset link...</p>
              </div>
            ) : success ? (
              <div className="space-y-4">
                <div className="bg-success/10 border border-success/30 rounded-xl p-5 flex items-start space-x-3">
                  <div className="w-8 h-8 bg-success/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-success" />
                  </div>
                  <div>
                    <p className="text-success font-semibold mb-1">Password Updated!</p>
                    <p className="text-success/80 text-sm">Your password has been successfully updated. Redirecting to sign in...</p>
                  </div>
                </div>
                <Link
                  to="/login"
                  className="inline-block w-full bg-primary text-primary-foreground font-bold text-base py-4 px-6 rounded-xl hover:opacity-90 transition-all text-center"
                >
                  Sign In Now
                </Link>
              </div>
            ) : !isValidSession ? (
              <div className="space-y-4">
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                  <p className="text-destructive text-sm">{error || 'Invalid or expired reset link.'}</p>
                </div>
                <Link
                  to="/forgot-password"
                  className="inline-block w-full bg-primary text-primary-foreground font-bold text-base py-4 px-6 rounded-xl hover:opacity-90 transition-all text-center"
                >
                  Request New Reset Link
                </Link>
              </div>
            ) : (
              <>
                {error && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 flex items-center space-x-3">
                    <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                    <p className="text-destructive text-sm">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="relative">
                    <Input
                      floating
                      label="New Password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e?.target?.value); if (error) setError(''); }}
                      required
                      className="w-full text-base touch-manipulation pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  <div className="relative">
                    <Input
                      floating
                      label="Confirm New Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e?.target?.value); if (error) setError(''); }}
                      required
                      className="w-full text-base touch-manipulation pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground space-y-1">
                    <p className="font-medium text-foreground">Password requirements:</p>
                    <p className={password?.length >= 8 ? 'text-success' : ''}>• At least 8 characters</p>
                    <p className={/(?=.*[a-zA-Z])(?=.*[0-9])/?.test(password) ? 'text-success' : ''}>• At least one letter and one number</p>
                    <p className={password && confirmPassword && password === confirmPassword ? 'text-success' : ''}>• Passwords match</p>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-primary text-primary-foreground py-4 sm:py-3 rounded-xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 text-base font-semibold min-h-[52px] touch-manipulation"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center space-x-2">
                        <span className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
                        <span>Updating Password...</span>
                      </span>
                    ) : 'Update Password'}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
